import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SyncIcon from '@mui/icons-material/Sync';
import ScienceIcon from '@mui/icons-material/Science';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalculateIcon from '@mui/icons-material/Calculate';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WorkspaceService from '../services/WorkspaceService';
import { viewTabTitle } from './workspaces/workspaceUtils';

// Kept in sync by hand with AtomApp.js's own WORKSPACE_ICON_MAP (not
// imported directly, to avoid a circular import between the two files) --
// a workspace whose icon isn't in this list just falls back to ViewModuleIcon.
const WORKSPACE_ICON_MAP = {
  MoveToInbox: MoveToInboxIcon,
  Inventory2: Inventory2Icon,
  LocalShipping: LocalShippingIcon,
  Settings: SettingsIcon,
  BarChart: BarChartIcon,
  Psychology: PsychologyIcon,
  Warehouse: WarehouseIcon,
  FactCheck: FactCheckIcon,
  Sync: SyncIcon,
  Science: ScienceIcon,
  Schedule: ScheduleIcon,
  Calculate: CalculateIcon,
  ViewModule: ViewModuleIcon,
  Handshake: HandshakeIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  ManageAccounts: ManageAccountsIcon,
  AccountTree: AccountTreeIcon,
  History: HistoryIcon,
  Lightbulb: LightbulbIcon,
};

/**
 * Full-screen workspace menu, opened from a center-top chip rather than
 * confined to a permanent left-sidebar panel. Initial view shows only what
 * the user has pinned (the "most useful" items); a second tab shows the
 * full permission-scoped tree (workspaces + their individual tabs/views)
 * with per-item pin toggles, for managing what shows up in the pinned view.
 * Selecting any item opens it and closes the panel.
 *
 * See project_sidebar_tab_level_pinning -- this replaces the old always-
 * visible "Supply Chain" section that mixed unrelated domain groups
 * (Administration, SYSTEM, TESTING, etc.) into one long always-open list.
 */
function WorkspaceMenuOverlay({
  open,
  onClose,
  colors,
  pinnedWorkspaces,
  workspaceGroups,
  onOpenWorkspace,
  onPinChanged, // called after any pin/unpin/reorder so the parent can refresh its own pinnedWorkspaces state
}) {
  const [tab, setTab] = useState('pinned');
  const [search, setSearch] = useState('');
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [expandedWorkspaces, setExpandedWorkspaces] = useState({});
  const [busyKey, setBusyKey] = useState(null); // workspaceType|viewId currently being pinned/unpinned

  useEffect(() => {
    if (!open) return;
    setTab('pinned');
    setSearch('');
  }, [open]);

  const loadAvailable = useCallback(async () => {
    setAvailableLoading(true);
    try {
      const workspaces = await WorkspaceService.getAvailableWorkspaces();
      setAvailableWorkspaces(workspaces || []);
    } catch (err) {
      console.error('Failed to fetch available workspaces:', err);
    } finally {
      setAvailableLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && tab === 'browse' && availableWorkspaces.length === 0 && !availableLoading) {
      loadAvailable();
    }
  }, [open, tab, availableWorkspaces.length, availableLoading, loadAvailable]);

  const groupMap = useMemo(() => {
    const map = {};
    (workspaceGroups || []).forEach((g) => { map[g.code] = g; });
    return map;
  }, [workspaceGroups]);

  const isPinned = useCallback((workspaceType, viewId) => {
    return (pinnedWorkspaces || []).some(
      (p) => p.workspaceType === workspaceType && (p.viewId || null) === (viewId || null)
    );
  }, [pinnedWorkspaces]);

  const key = (workspaceType, viewId) => `${workspaceType}::${viewId || ''}`;

  const handleTogglePin = useCallback(async (workspaceType, viewId, label) => {
    const k = key(workspaceType, viewId);
    setBusyKey(k);
    try {
      if (isPinned(workspaceType, viewId)) {
        await WorkspaceService.unpinWorkspace(workspaceType, viewId);
      } else {
        await WorkspaceService.pinWorkspace(workspaceType, null, viewId);
      }
      await onPinChanged();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    } finally {
      setBusyKey(null);
    }
  }, [isPinned, onPinChanged]);

  const handleMovePin = useCallback(async (idx, direction) => {
    const list = [...(pinnedWorkspaces || [])];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
    try {
      await WorkspaceService.reorderWorkspaces(
        list.map((p) => ({ workspaceType: p.workspaceType, viewId: p.viewId || null }))
      );
      await onPinChanged();
    } catch (err) {
      console.error('Failed to reorder pins:', err);
    }
  }, [pinnedWorkspaces, onPinChanged]);

  const handleSelect = useCallback((workspaceType, title, viewId) => {
    onOpenWorkspace(workspaceType, title, viewId ? { viewId } : null);
    onClose();
  }, [onOpenWorkspace, onClose]);

  // ---- Pinned tab: grouped by domain, same grouping rule as before ----
  const pinnedGrouped = useMemo(() => {
    const grouped = {};
    (pinnedWorkspaces || []).forEach((ws) => {
      const domain = ws.domain || 'OTHER';
      if (!grouped[domain]) grouped[domain] = [];
      grouped[domain].push(ws);
    });
    const sortedDomains = Object.keys(grouped).sort((a, b) => {
      const aOrder = groupMap[a]?.sortOrder ?? 99;
      const bOrder = groupMap[b]?.sortOrder ?? 99;
      return aOrder - bOrder;
    });
    return { grouped, sortedDomains };
  }, [pinnedWorkspaces, groupMap]);

  // ---- Browse tab: grouped by domain, filtered by search ----
  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableWorkspaces;
    return availableWorkspaces
      .map((ws) => {
        // Also match the workspace description -- see
        // project_workspace_search_tab_extraction_fix.
        const titleMatches = ws.title?.toLowerCase().includes(q);
        const descriptionMatches = ws.description?.toLowerCase().includes(q);
        const matchingViews = (ws.views || []).filter((v) => v.label?.toLowerCase().includes(q));
        if (titleMatches || descriptionMatches) return ws;
        if (matchingViews.length > 0) return { ...ws, views: matchingViews };
        return null;
      })
      .filter(Boolean);
  }, [availableWorkspaces, search]);

  const browseGrouped = useMemo(() => {
    const grouped = {};
    filteredAvailable.forEach((ws) => {
      const domain = ws.domain || 'OTHER';
      if (!grouped[domain]) grouped[domain] = [];
      grouped[domain].push(ws);
    });
    const sortedDomains = Object.keys(grouped).sort((a, b) => {
      const aOrder = groupMap[a]?.sortOrder ?? 99;
      const bOrder = groupMap[b]?.sortOrder ?? 99;
      return aOrder - bOrder;
    });
    return { grouped, sortedDomains };
  }, [filteredAvailable, groupMap]);

  const renderIcon = (iconName) => {
    const IconComp = WORKSPACE_ICON_MAP[iconName] || ViewModuleIcon;
    return <IconComp sx={{ fontSize: 18, color: '#8b5cf6', flexShrink: 0 }} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: '80vh',
          bgcolor: colors.background,
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <ViewModuleIcon sx={{ color: '#8b5cf6' }} />
          <Typography variant="h6" sx={{ color: colors.textPrimary, flexGrow: 1 }}>
            Menu
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.textMuted }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ borderBottom: `1px solid ${colors.border}`, px: 2, minHeight: 40 }}
        >
          <Tab value="pinned" label={`Pinned (${(pinnedWorkspaces || []).length})`} sx={{ minHeight: 40 }} />
          <Tab value="browse" label="All Workspaces / Manage Pins" sx={{ minHeight: 40 }} />
        </Tabs>

        {tab === 'browse' && (
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search workspaces and tabs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {tab === 'pinned' ? (
            pinnedWorkspaces.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: colors.textMuted, mb: 1 }}>
                  Nothing pinned yet.
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => setTab('browse')}
                  sx={{ color: '#8b5cf6', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Browse all workspaces to pin some
                </Typography>
              </Box>
            ) : (
              pinnedGrouped.sortedDomains.map((domain) => (
                <Box key={domain} sx={{ mb: 2 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
                  >
                    {groupMap[domain]?.label || domain}
                  </Typography>
                  {pinnedGrouped.grouped[domain].map((ws) => {
                    const idx = pinnedWorkspaces.indexOf(ws);
                    return (
                      <Box
                        key={`${ws.workspaceType}::${ws.viewId || ''}`}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1,
                          cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                        }}
                        onClick={() => handleSelect(ws.workspaceType, ws.customLabel || ws.title, ws.viewId)}
                      >
                        {renderIcon(ws.icon)}
                        <Typography variant="body2" sx={{ color: colors.textPrimary, flex: 1 }}>
                          {ws.customLabel || ws.title}
                        </Typography>
                        <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" disabled={idx === 0} onClick={() => handleMovePin(idx, 'up')}>
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton size="small" disabled={idx === pinnedWorkspaces.length - 1} onClick={() => handleMovePin(idx, 'down')}>
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                          <Tooltip title="Unpin">
                            <IconButton
                              size="small"
                              disabled={busyKey === key(ws.workspaceType, ws.viewId)}
                              onClick={() => handleTogglePin(ws.workspaceType, ws.viewId, ws.customLabel || ws.title)}
                              sx={{ color: '#8b5cf6' }}
                            >
                              {busyKey === key(ws.workspaceType, ws.viewId) ? <CircularProgress size={14} /> : <PushPinIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))
            )
          ) : availableLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            browseGrouped.sortedDomains.map((domain) => {
              const domainOpen = expandedDomains[domain] !== false; // default expanded
              return (
                <Box key={domain} sx={{ mb: 1 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', py: 0.5 }}
                    onClick={() => setExpandedDomains((p) => ({ ...p, [domain]: !domainOpen }))}
                  >
                    <IconButton size="small" sx={{ p: 0, color: colors.textMuted }}>
                      {domainOpen ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                    <Typography variant="overline" sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                      {groupMap[domain]?.label || domain}
                    </Typography>
                  </Box>
                  {domainOpen && browseGrouped.grouped[domain].map((ws) => {
                    const hasViews = (ws.views || []).length > 0;
                    const wsExpanded = expandedWorkspaces[ws.workspaceCode];
                    const wsPinned = isPinned(ws.workspaceCode, null);
                    return (
                      <Box key={ws.workspaceCode} sx={{ pl: 3 }}>
                        <Box
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1,
                            cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                          }}
                        >
                          {hasViews ? (
                            <IconButton
                              size="small"
                              sx={{ p: 0, color: colors.textMuted }}
                              onClick={() => setExpandedWorkspaces((p) => ({ ...p, [ws.workspaceCode]: !wsExpanded }))}
                            >
                              {wsExpanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          ) : <Box sx={{ width: 24 }} />}
                          {renderIcon(ws.icon)}
                          <Typography
                            variant="body2"
                            sx={{ color: colors.textPrimary, flex: 1 }}
                            onClick={() => handleSelect(ws.workspaceCode, ws.title, null)}
                          >
                            {ws.title}
                          </Typography>
                          <Tooltip title={wsPinned ? 'Unpin' : 'Pin whole workspace'}>
                            <IconButton
                              size="small"
                              disabled={busyKey === key(ws.workspaceCode, null)}
                              onClick={() => handleTogglePin(ws.workspaceCode, null, ws.title)}
                              sx={{ color: wsPinned ? '#8b5cf6' : colors.textMuted }}
                            >
                              {busyKey === key(ws.workspaceCode, null) ? <CircularProgress size={14} /> :
                                wsPinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {hasViews && wsExpanded && ws.views.map((v) => {
                          const vPinned = isPinned(ws.workspaceCode, v.viewId);
                          return (
                            <Box
                              key={v.viewId}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: 5, pr: 1, borderRadius: 1,
                                cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: colors.textSecondary, flex: 1, fontSize: '0.82rem' }}
                                onClick={() => handleSelect(ws.workspaceCode, viewTabTitle(ws.title, v.label), v.viewId)}
                              >
                                {v.label}
                              </Typography>
                              <Tooltip title={vPinned ? 'Unpin this tab' : 'Pin this tab'}>
                                <IconButton
                                  size="small"
                                  disabled={busyKey === key(ws.workspaceCode, v.viewId)}
                                  onClick={() => handleTogglePin(ws.workspaceCode, v.viewId, v.label)}
                                  sx={{ color: vPinned ? '#8b5cf6' : colors.textMuted }}
                                >
                                  {busyKey === key(ws.workspaceCode, v.viewId) ? <CircularProgress size={14} /> :
                                    vPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default WorkspaceMenuOverlay;
